/**
 * Medidas y límites para iconos del CMS AGDYSA.
 * Preferido: PNG/WebP transparente 128×128, ≤200 KB.
 */
import { ICON_CATALOG, ICON_CATEGORIES } from "@agdysa/icons";

export const CMS_ICON_SPECS = {
  preferredSize: 128,
  minSize: 64,
  maxSize: 256,
  maxBytes: 200 * 1024,
  accept: "image/png,image/webp,image/svg+xml,image/jpeg",
  formatsLabel: "PNG, WebP o SVG (fondo transparente preferible)",
  sizeLabel: "128×128 px (mín. 64×64, máx. 256×256)",
  weightLabel: "Máximo 200 KB",
};

/**
 * Etiquetas de **solo iconos personalizados subidos** (Firestore `cmsIcons`).
 *
 * NO son las secciones del catálogo (Presets redes, Marca AGDYSA, Marca LABSYSO,
 * Lucide/React Icons). Esas fuentes son fijas del sistema.
 *
 * Sirven para filtrar archivos subidos en el IconPicker según el editor
 * (Footer → redes, Menú responsivo → navegacion, etc.).
 *
 * `agdysa` / `labsyso` = archivos de marca propios del cliente (aparte del
 * paquete sistema “Iconos de marca AGDYSA/LABSYSO”, que no se modifica al subir).
 */
export const CMS_ICON_CLASSIFICATIONS = {
  redes: "Redes sociales",
  navegacion: "Menú responsivo",
  agdysa: "AGDYSA",
  labsyso: "LABSYSO",
  contenido: "Contenido / páginas",
  servicios: "Servicios",
  general: "General",
};

/** Texto de ayuda (dónde se usa cada etiqueta al subir). */
export const CMS_ICON_CLASSIFICATION_HINTS = {
  redes: "Footer y redes de contacto: pestaña Redes del selector.",
  navegacion: "Menú hamburguesa y barra inferior móvil.",
  agdysa: "Archivos de marca AGDYSA del cliente (además del paquete oficial).",
  labsyso: "Archivos de marca LABSYSO del cliente (además del paquete oficial).",
  contenido: "Home, Nosotros (valores), Credibilidad y páginas generales.",
  servicios: "Página y tarjetas de Servicios / capacitaciones.",
  general: "Uso libre; aparece en Subidos sin filtro específico.",
};

export const DEFAULT_CMS_ICON_CLASSIFICATION = "general";

/**
 * Alias legacy del JSON Home → id canónico @agdysa/icons
 * (contenido antiguo sigue funcionando).
 */
export const LEGACY_ICON_ALIASES = {
  droplet: "hidratacion",
  pause: "descansos",
  shirt: "ropa-adecuada",
  clock: "horarios",
  sun: "proteccion-solar",
  utensils: "alimentacion",
  flask: "laboratorio-clinico",
  shield: "mediciones-higienicas",
  chart: "gestion-riesgo",
};

/** Todo el catálogo de marca (oficial + extended + alias), sin duplicar aliases en UI */
export const BRAND_ICON_OPTIONS = ICON_CATALOG.filter((i) => i.source !== "alias").map((i) => ({
  key: i.id,
  brandId: i.id,
  label: i.label,
  category: i.category,
  pending: !!i.pending,
  tags: Array.isArray(i.tags) ? i.tags : [],
}));

/** Subconjunto de marca orientado a LABSYSO (tag labsyso en @agdysa/icons). */
export const LABSYSO_BRAND_ICON_OPTIONS = BRAND_ICON_OPTIONS.filter((i) =>
  i.tags.includes("labsyso")
);

export const BRAND_ICON_CATEGORIES = ICON_CATEGORIES;

export function resolveBrandIconId(iconKey) {
  if (!iconKey || typeof iconKey !== "string") return null;
  if (/^https?:\/\//i.test(iconKey)) return null;
  return LEGACY_ICON_ALIASES[iconKey] || iconKey;
}

export function findBrandIconOption(iconKey) {
  const id = resolveBrandIconId(iconKey);
  if (!id) return null;
  return BRAND_ICON_OPTIONS.find((o) => o.key === id) || null;
}

export function isIconUrl(value) {
  return typeof value === "string" && /^https?:\/\//i.test(value);
}

export function normalizeCmsIconClassification(value) {
  const key = String(value || "").trim();
  if (key && CMS_ICON_CLASSIFICATIONS[key]) return key;
  return DEFAULT_CMS_ICON_CLASSIFICATION;
}

export function validateCmsIconFile(file) {
  if (!file) return "Archivo requerido";
  if (!file.type.startsWith("image/")) return "Solo imágenes (PNG, WebP, SVG, JPEG)";
  if (file.size > CMS_ICON_SPECS.maxBytes) {
    return `Máximo ${Math.round(CMS_ICON_SPECS.maxBytes / 1024)} KB`;
  }
  return null;
}
